// src/page/SummaryView.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

export default function SummaryView() {
    useAuthGuard();

    // ===== utilities =====
    const pad2 = (n) => String(n).padStart(2, '0');
    const fmtYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const fmtDisp = (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d)) return '';
        return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    };

    // === イベント選択 ===
    const [eventId, setEventId] = useState('');
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const eventOptions = useMemo(
        () => (Array.isArray(events) ? events : [])
            .map(e => ({
                value: e.eventId ?? e.event_id ?? '',
                label: e.eventName ?? e.event_name ?? '',
            }))
            .filter(o => o.value && o.label),
        [events]
    );
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingEvents(true);
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/event`);
                url.searchParams.set('page', '1');
                url.searchParams.set('size', '200');
                url.searchParams.set('sort', 'title');
                url.searchParams.set('dir', 'asc');
                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                setEvents(Array.isArray(data.items) ? data.items : []);
            } catch {
                setEvents([]);
            } finally {
                setLoadingEvents(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // === イベントに紐づくスポット選択 ===
    const [spotId, setSpotId] = useState('');
    const [eventSpots, setEventSpots] = useState([]); // [{value, label}]
    const [loadingEventSpots, setLoadingEventSpots] = useState(false);
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            setSpotId('');
            setEventSpots([]);
            if (!eventId) return;
            try {
                setLoadingEventSpots(true);
                const token = localStorage.getItem('access_token') || '';
                const res = await fetch(`${API_BASE_URL}/v1/event/${encodeURIComponent(eventId)}`, {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json().catch(() => ({}));
                // j.related.pairs => [{ spot:{ID,Name}, package:{...} }, ...]
                const pairs = j?.related?.pairs;
                if (Array.isArray(pairs)) {
                    const seen = new Set();
                    const opts = [];
                    for (const p of pairs) {
                        const id = p?.spot?.ID || '';
                        const name = p?.spot?.Name || '';
                        if (id && name && !seen.has(id)) {
                            seen.add(id);
                            opts.push({ value: id, label: name });
                        }
                    }
                    setEventSpots(opts);
                } else {
                    setEventSpots([]);
                }
            } catch {
                setEventSpots([]);
            } finally {
                setLoadingEventSpots(false);
            }
        })();
        return () => ac.abort();
    }, [eventId]);

    // === flatpickr for 照会期間 ===
    const rangeRef = useRef(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    const rangeText = fromDate && toDate ? `${fmtDisp(fromDate)} ~ ${fmtDisp(toDate)}` : '';

    useEffect(() => {
        if (!window.flatpickr) return;
        const ja = window.flatpickr.l10ns?.ja || {};
        const el = document.getElementById('summary_period_range');
        if (!el) return;

        const fp = window.flatpickr(el, {
            mode: 'range',
            dateFormat: 'Y/m/d',
            locale: { ...ja, rangeSeparator: ' ~ ' },
            onChange: (dates) => {
                if (dates?.length === 2) {
                    setFromDate(dates[0]);
                    setToDate(dates[1]);
                } else if (!dates?.length) {
                    setFromDate('');
                    setToDate('');
                }
            }
        });
        rangeRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);

    // === free word: user number ===
    const [userNumber, setUserNumber] = useState('');

    // === screen types (checkbox) ===
    // code => label
    const SCREEN_TYPES = {
        C00701: 'イベント一覧',
        C00702: 'イベント詳細',
        C00703: 'スポットサービス',
        C00704: 'スポットイベント',
        C00705: 'スポットお知らせ',
        C00706: 'スポットピックアップニュース',
        C00714: 'スポット詳細',
        C00715: '関連リンク',
        C00716: 'キャンペーン応募',
    };
    const [screenChecks, setScreenChecks] = useState(
        Object.keys(SCREEN_TYPES).reduce((m, k) => (m[k] = false, m), {})
    );
    // 画面区分コード → 日本語ラベル
    const screenLabel = (code) => {
        if (!code) return '';
        return SCREEN_TYPES[String(code)] ?? String(code);
    };
    // === committed query states ===
    const [qEventId, setQEventId] = useState('');
    const [qSpotId, setQSpotId] = useState('');
    const [qUserNumber, setQUserNumber] = useState('');
    const [qFrom, setQFrom] = useState(null); // Date|null
    const [qTo, setQTo] = useState(null);     // Date|null
    const [qScreens, setQScreens] = useState(''); // "C00701,C00702"

    // === list state ===
    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');

    // paging
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    // ページャ表示を凝縮（例: < 1 … 10 11 [12] 13 14 … 200 >）
    const buildPageItems = (current, totalPages, window = 5) => {
        // window: 現在ページの両側に表示する件数
        if (totalPages <= 1) return [1];
        const items = [];
        const add = (v) => {
            if (items.length === 0 || items[items.length - 1] !== v) items.push(v);
        };
        const left = Math.max(1, current - window);
        const right = Math.min(totalPages, current + window);
        add(1);
        if (left > 2) add('…');
        for (let p = left; p <= right; p++) add(p);
        if (right < totalPages - 1) add('…');
        if (totalPages > 1) add(totalPages);
        return items;
    };
    const pageItems = useMemo(() => buildPageItems(page, pageCount, 5), [page, pageCount]);

    // sorting (API: date|user|event|spot|screen)
    const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
    const toggleSort = (key) => setSort(prev =>
        prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );

    // fetch list
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setListLoading(true);
                setListError('');
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/summary/view`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('sort', sort.key);
                url.searchParams.set('dir', sort.dir);
                if (qEventId) url.searchParams.set('eventid', qEventId);
                if (qSpotId) url.searchParams.set('spotid', qSpotId);
                if (qUserNumber) url.searchParams.set('usernumber', qUserNumber);
                if (qFrom && qTo) {
                    url.searchParams.set('from', fmtYMD(qFrom)); // LIST expects YYYY-MM-DD
                    url.searchParams.set('to', fmtYMD(qTo));
                }
                if (qScreens) url.searchParams.set('screen', qScreens); // optional (backend may ignore or use if implemented)

                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json().catch(() => ({}));
                setRows(Array.isArray(j.items) ? j.items : []);
                setTotal(Number(j.total) || 0);
                if (j.size && Number(j.size) !== pageSize) setPageSize(Number(j.size));
            } catch (e) {
                if (e?.name === 'AbortError') return;
                console.error(e);
                setRows([]);
                setListError('読み込みに失敗しました');
            } finally {
                setListLoading(false);
            }
        })();
        return () => ac.abort();
    }, [page, pageSize, sort, qEventId, qSpotId, qUserNumber, qFrom, qTo, qScreens]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    // submit & clear
    const handleSubmit = (e) => {
        e.preventDefault();
        setQEventId(eventId || '');
        setQSpotId(spotId || '');
        setQUserNumber(userNumber.trim());
        setQFrom(fromDate || null);
        setQTo(toDate || null);
        const selected = Object.entries(screenChecks)
            .filter(([, v]) => v)
            .map(([k]) => k)
            .join(',');
        setQScreens(selected);
        setPage(1);
    };

    const handleClear = () => {
        setEventId('');
        setSpotId('');
        setUserNumber('');
        setFromDate('');
        setToDate('');
        setQEventId('');
        setQSpotId('');
        setQUserNumber('');
        setQFrom(null);
        setQTo(null);
        setQScreens('');
        setScreenChecks(Object.keys(SCREEN_TYPES).reduce((m, k) => (m[k] = false, m), {}));
        setPage(1);
    };

    // CSV export
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token') || '';
            const url = new URL(`${API_BASE_URL}/v1/summary/view/export`);
            url.searchParams.set('sort', sort.key);
            url.searchParams.set('dir', sort.dir);
            if (qEventId) url.searchParams.set('eventid', qEventId);
            if (qSpotId) url.searchParams.set('spotid', qSpotId);
            if (qUserNumber) url.searchParams.set('usernumber', qUserNumber);
            if (qFrom && qTo) {
                const s = new Date(qFrom.getFullYear(), qFrom.getMonth(), qFrom.getDate(), 0, 0, 0, 0);
                const e = new Date(qTo.getFullYear(), qTo.getMonth(), qTo.getDate(), 23, 59, 59, 999);
                url.searchParams.set('from', s.toISOString()); // export expects RFC3339
                url.searchParams.set('to', e.toISOString());
            }
            if (qScreens) url.searchParams.set('screen', qScreens);

            const res = await fetch(url.toString(), {
                headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const d = new Date();
            const fname = `閲覧実績_${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}.csv`;
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.error(e);
            alert('CSVのダウンロードに失敗しました');
        }
    };

    // ===== Render =====
    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/summary">実績管理</a></li>
                    <li className="breadcrumb-item active">閲覧実績</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">閲覧実績</h2>
                    </div>
                </div>

                <div id="summary">
                    <div className="mb-4">
                        <form className="position-relative w-100" onSubmit={handleSubmit}>
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div className="search-box" style={{ minWidth: 280 }}>
                                    <label className="form-label mb-1 d-block">イベント名</label>
                                    <Select
                                        options={eventOptions}
                                        value={eventOptions.find(o => o.value === eventId) || null}
                                        onChange={(opt) => setEventId(opt?.value || '')}
                                        isDisabled={loadingEvents}
                                        isClearable
                                        placeholder={loadingEvents ? '読み込み中…' : 'イベント選択'}
                                        classNamePrefix="react-select"
                                        className="w-100"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            container: (b) => ({ ...b, width: '100%', minWidth: 0 }),
                                            menuPortal: (b) => ({ ...b, zIndex: 9999 })
                                        }}
                                        filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                                <div className="search-box" style={{ minWidth: 280 }}>
                                    <label className="form-label mb-1 d-block">スポット名</label>
                                    <Select
                                        options={eventSpots}
                                        value={eventSpots.find(o => o.value === spotId) || null}
                                        onChange={(opt) => setSpotId(opt?.value || '')}
                                        isDisabled={!eventId || loadingEventSpots}
                                        isClearable
                                        placeholder={!eventId ? 'イベントを選択してください' : (loadingEventSpots ? '読み込み中…' : 'スポット選択')}
                                        classNamePrefix="react-select"
                                        className="w-100"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            container: (b) => ({ ...b, width: '100%', minWidth: 0 }),
                                            menuPortal: (b) => ({ ...b, zIndex: 9999 })
                                        }}
                                        filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                                <div className="search-box">
                                    <label className="form-label mb-1 d-block">照会期間</label>
                                    <div className="position-relative">
                                        <input
                                            id="summary_period_range"
                                            className="form-control search-input search pe-5"
                                            type="text"
                                            name=""
                                            placeholder="YYYY/MM/DD ~ YYYY/MM/DD"
                                            value={rangeText}
                                            readOnly
                                        />
                                        <span
                                            className="fas fa-calendar-alt search-box-icon position-absolute top-50 translate-middle-y"
                                            style={{ left: 12, pointerEvents: 'none' }}
                                        />
                                    </div>
                                </div>
                                <div className="search-box">
                                    <input
                                        aria-label="Search"
                                        className="form-control search-input search"
                                        placeholder="会員番号"
                                        type="search"
                                        value={userNumber}
                                        onChange={(e) => setUserNumber(e.target.value)}
                                    />
                                    <span className="fas fa-search search-box-icon"></span>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                                <div>
                                    <label className="form-label mb-1 d-block">画面区分</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {Object.entries(SCREEN_TYPES).map(([code, label]) => (
                                            <div className="form-check" key={code}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={!!screenChecks[code]}
                                                    onChange={(e) =>
                                                        setScreenChecks(s => ({ ...s, [code]: e.target.checked }))}
                                                />
                                                <label className="form-check-label">{label}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3 text-nowrap">ユーザーID</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('user'); setPage(1); }}>
                                            会員番号<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('screen'); setPage(1); }}>
                                            画面区分<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('event'); setPage(1); }}>
                                            イベント名<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('spot'); setPage(1); }}>
                                            スポット名<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button" onClick={() => { toggleSort('date'); setPage(1); }}>
                                            閲覧日時<span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listError && (
                                        <tr><td className="ps-3 text-danger" colSpan={6}>{listError}</td></tr>
                                    )}
                                    {!listError && listLoading && (
                                        <tr><td className="ps-3 text-body-tertiary" colSpan={6}>読み込み中…</td></tr>
                                    )}
                                    {!listError && !listLoading && rows.length === 0 && (
                                        <tr><td className="ps-3 text-body-tertiary" colSpan={6}>データがありません</td></tr>
                                    )}
                                    {!listError && !listLoading && rows.map((r, i) => (
                                        <tr key={i}>
                                            <td className="ps-3">{r.userId || ''}</td>
                                            <td className="ps-3">{r.userNumber || ''}</td>
                                            <td className="ps-3">{screenLabel(r.screenType)}</td>
                                            <td className="ps-3">{r.eventName || ''}</td>
                                            <td className="ps-3">{r.spotName || ''}</td>
                                            <td className="ps-3">{fmtDateTime(r.browseDate)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>
                                <ul className="mb-0 pagination">
                                    {pageItems.map((it, idx) =>
                                        it === '…' ? (
                                            <li key={`ellipsis-${idx}`} className="page-item disabled">
                                                <span className="page-link">…</span>
                                            </li>
                                        ) : (
                                            <li key={it} className={`page-item ${it === page ? 'active' : ''}`}>
                                                <button type="button" className="page-link" onClick={() => setPage(it)}>
                                                    {it}
                                                </button>
                                            </li>
                                        )
                                    )}
                                </ul>
                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </Layout>
    );
}
